/**
 * busyForm — the keyboard and focus contract for a form whose submit runs a
 * request (a dialog's create / save / verify form), and for a lone button that
 * runs one (a confirm's "Start the test", an editor's "Save goal").
 *
 * A browser drops focus from a control the moment it becomes `disabled`. These
 * forms disabled their field and their button while the request ran, so Enter
 * in the field, or a press of the button, threw a keyboard or screen-reader
 * user onto <body>. A request that then failed showed its message with focus
 * nowhere, and the next Tab started from the lost spot and skipped the field to
 * fix. So neither control is ever natively disabled here:
 *
 *   - While `busy`, a field is readOnly + aria-disabled. It keeps focus, is
 *     announced as unavailable, and takes typing again when the request
 *     settles. That suits a <textarea> or an <input> of a type that honours
 *     readOnly (text, date, number and the like) only: it means nothing on a
 *     select, a checkbox or a radio, which would stay operable while announced
 *     as unavailable.
 *   - The button that runs the request is aria-disabled while busy.
 *   - With nothing disabled, the guard here is what refuses a second request:
 *     Enter in a read-only field still submits its form, and an aria-disabled
 *     button still clicks. The guard reads `busy` on the next event, so the
 *     handler has to set it in the event that starts the request, before its
 *     first await. That covers a person: every key press and pointer press is
 *     a task of its own, and React has rendered `busy` before the next one. It
 *     does not cover two submits (or clicks) dispatched from script in ONE
 *     task: both see `busy` unset and both run the handler.
 *   - A held Enter is refused as well. Focus now survives a failed request, so
 *     the key's auto-repeat would send it again the moment it settled; and a
 *     button that has focus as its dialog opens would be pressed by the same
 *     hold that opened the dialog.
 *
 * Controls a request can't start from (Cancel, "Not now", the goal editor's
 * fields) are still natively disabled for the wait. That is enough wherever
 * a request starts with focus on what started it: in the field Enter was
 * pressed in, or on the button that was pressed. It is NOT enough in a browser
 * that gives focus to no button pressed with a pointer (Safari is one). There
 * a pointer-started request leaves focus where it was; a read-only field keeps
 * it, but if it was resting on one of those controls, disabling that control
 * drops it. A control that can hold focus when a request starts should take
 * busyButton instead. Style the unavailable look off [aria-disabled="true"],
 * never :disabled. In a dialog, pair this with <Modal busy> (Escape and the
 * scrim can't dismiss mid-request) and a <PendingStatus> (the wait is
 * announced).
 *
 * BUSY MUST END. A dialog held open by its own request has no exit until that
 * request settles, so the request needs a deadline: give it axios's `timeout`
 * (config.js HELD_REQUEST_TIMEOUT_MS, a limit per attempt). A stalled
 * connection then ends as a failure the form deals with (its message; or, in
 * Classrooms, closing the dialog) instead of a dialog nobody can leave.
 *
 * Spread the props onto the form's own markup, and don't also give those
 * elements onSubmit, onClick, onKeyDown, readOnly, aria-disabled or type:
 *
 *   const form = busyForm({ busy: saving, onSubmit: handleSave });
 *   <form {...form.formProps}>
 *     <input ref={nameRef} value={name} onChange={...} {...form.fieldProps} />
 *     <Button {...form.submitProps}>Save</Button>
 *   </form>
 *
 *   <Button {...busyButton({ busy: starting, onClick: onConfirm })}>Start</Button>
 *
 * busyButton also suits a control that has to be refused while busy and can't
 * be natively disabled, such as a link out of the dialog.
 *
 * When the answer refuses what a field holds (a 422 date, a one-time code that
 * is wrong or spent), send the person back to it with useRefusedFieldFocus,
 * below.
 */
import { useCallback, useLayoutEffect, useState } from 'react';
import refuseHeldEnter from '../../utils/refuseHeldEnter';
import { topModal } from './modalStack';

/**
 * onKeyDown for a whole form: the shared guard (utils/refuseHeldEnter cancels
 * Enter's auto-repeat, and with it the implicit submission from a field and
 * the click on a focused button), for every control in the form but one. In a
 * <textarea> a held Enter is typing (it repeats newlines and submits nothing),
 * so it is left alone there.
 */
function refuseHeldEnterInForm(event) {
  if (event.target instanceof HTMLTextAreaElement) return;
  refuseHeldEnter(event);
}

export function busyForm({ busy, onSubmit }) {
  return {
    formProps: {
      onSubmit: (event) => {
        event.preventDefault();
        if (!busy) onSubmit(event);
      },
      onKeyDown: refuseHeldEnterInForm,
    },
    fieldProps: {
      readOnly: busy,
      'aria-disabled': busy || undefined,
    },
    submitProps: {
      type: 'submit',
      'aria-disabled': busy || undefined,
    },
  };
}

export function busyButton({ busy, onClick }) {
  return {
    'aria-disabled': busy || undefined,
    onClick: (event) => {
      if (busy) {
        event.preventDefault();
        return;
      }
      onClick?.(event);
    },
    onKeyDown: refuseHeldEnter,
  };
}

/**
 * useRefusedFieldFocus — after a failure whose message is about a field, puts
 * focus in that field. Call the function it returns from the handler that
 * stores the message:
 *
 *   const focusRefused = useRefusedFieldFocus(dateRef);
 *   ...catch (err) { setError('That date doesn’t look right.'); focusRefused(); }
 *
 * The move waits for that render to commit (a layout effect). By then the
 * field takes typing again and carries its aria-invalid and aria-describedby,
 * so a screen reader arriving on it reads the field to fix, not the read-only,
 * unavailable one it was while the request ran.
 *
 * It takes focus from wherever the person went while the request ran: the
 * answer needs them at the field. (An answer that asks nothing of them, a
 * success, should leave a control they moved to alone: hooks/useGateFocus.)
 *
 * It is skipped while another dialog is open over this form (a gate the
 * interceptor raised mid-request, a timer-driven summary): that dialog owns
 * focus, and closing it returns focus to the control it was on in this form.
 *
 * A failure that says nothing about the field (no answer at all, a 5xx)
 * should not call it: focus stays on the control the person will press again.
 */
export function useRefusedFieldFocus(fieldRef) {
  const [refusals, setRefusals] = useState(0);

  useLayoutEffect(() => {
    if (refusals === 0) return;
    const field = fieldRef.current;
    if (!field) return;
    const top = topModal();
    if (top && !top.contains(field)) return;
    field.focus();
  }, [refusals, fieldRef]);

  // A count, not a flag: a second refusal with the very same message changes
  // no other state, and still has to send the person back to the field.
  return useCallback(() => setRefusals((n) => n + 1), []);
}
